<template>
  <res-dialog :showDialog="showDialog"
              :maxWidth="maxWidth"
              :setWidth="setWidth"
              title=""
              titleClass="resBlue darken-1 white--text"
              :showTitleIcon="false"
              :titleIconSize="17"
              iconColor="white"
              :cancelButtonOnly="errorState || past"
              saveButtonText="APPLY"
              :saveDisabled="saveDisabled"
              :cancelButtonText="past ? 'OK' : 'CANCEL'"
              cancelButtonWidth="80"
              :defaultButtonAction="confirm"
              @save="confirm"
              @cancel="cancel">
    <v-card-text>

      <v-row v-if="errorState">
        <v-col sm="10">
          The <span class="resOrange--text text--darken-2">entire</span> distribution <span class="resOrange--text text--darken-2">cannot</span> be performed because:
        </v-col>
        <v-col sm="2">
          <div class="help-controls step-down" @click="openHelp(null)">
            <span class="help-label">Help</span>
            <v-icon color="var(--v-resBlue-darken1)" class="pb-1">mdi-help-circle</v-icon>
          </div>
        </v-col>
      </v-row>
      <v-row v-if="errorState">
        <v-col>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">
                    Property
                  </th>
                  <th class="text-left">
                    Error
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in formattedErrors" :key="i">
                  <td>{{ item.propertyAbbreviation }}</td>
                  <td><span class="bulk-creation-orange">{{ item.error }}</span></td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
      </v-row>
      <v-row v-if="errorState" class="font-weight-500">
        <v-col>
          <strong class="ml-3">
            Please correct any errors or remove properties with errors and try again.
          </strong>
        </v-col>
      </v-row>

      <v-row v-if="!errorState">
        <v-col sm="10">
          <strong>The entire distribution {{methodText}} {{applicationText}}</strong>
        </v-col>
        <v-col sm="2">
          <div class="help-controls step-down" @click="openHelp(null)">
            <span class="help-label">Help</span>
            <v-icon color="var(--v-resBlue-darken1)" class="pb-1">mdi-help-circle</v-icon>
          </div>
        </v-col>
      </v-row>
      <v-row v-if="!errorState">
        <v-col>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>
                    Property
                  </th>
                  <th class="text-left">
                    Budget Version (most recently edited)
                  </th>
                  <th class="text-left">
                    GL Account
                  </th>
                  <th>
                    Units
                  </th>
                  <th v-if="showPercent">
                    %
                  </th>
                  <th>
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in bulkApplications" :key="i">
                  <td class="subtle resGrey--text">{{ item.propertyAbbreviation }}</td>
                  <td class="subtle resGrey--text text-left">{{ item.budgetVersion }}</td>
                  <td class="subtle resGrey--text text-left">{{ item.glAccountName }}</td>
                  <td class="subtle resGrey--text">{{ item.units }}</td>
                  <td class="subtle resGrey--text" v-if="showPercent">{{ Math.round(item.percent * 100, 2) / 100 }}%</td>
                  <td class="subtle resGrey--text">${{ item.amount.toFixed(2) }}</td>
                </tr>
                <tr>
                  <td class="no-border-right"><strong>Total:</strong></td>
                  <td class="no-border-right no-border-left"></td>
                  <td class="no-border-left"></td>
                  <td><strong>{{totalUnits}}</strong></td>
                  <td v-if="showPercent"><strong>{{totalPercent}}%</strong></td>
                  <td><strong>${{totalAmount.toFixed(2)}}</strong></td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <v-simple-table>
            <template v-slot:default>
              <tbody>
                <tr class="pa-0 ma-0">
                  <td class="resGrey--text subtle short no-border-right">Assumption Statement Text:</td>
                  <td class="no-border-left"><v-textarea auto-grow rows="1" :readonly="past" v-model="assumptionStatementInternal" :rules="[v => (v || '').length <= 100 || 'Assumption statement must be 100 characters or less.', v => ((v || '').trim().length >= 3 || method != 4) || 'Assumption statement must be 3 characters or more.']"></v-textarea></td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
      </v-row>

    </v-card-text>
  </res-dialog>
</template>

<script>
  import ResDialog from '@/components/base/ResDialog.vue';

  export default {
    components: {
      ResDialog
    },
    props: {
      showDialog: {
        type: Boolean
      },
      maxWidth: null,
      setWidth: null,
      past: {
        type: Boolean,
        default: false
      },
      method: {
        type: Number,
        default: 0
      },
      assumptionStatement: {
        type: String,
        default: ''
      },
      errors: {
        type: Array,
        default: () => []
      },
      bulkApplications: {
        type: Array,
        default: () => []
      }
    },
    data() {
      return {
        isSaveButtonDisabled: false,
        assumptionStatementInternal: ''
      }
    },
    methods: {
      cancel() {
        this.isSaveButtonDisabled = true;
        this.$emit('cancel', true)
      },
      confirm() {
        this.isSaveButtonDisabled = true;
        this.$emit('confirm', this.assumptionStatementInternal)
      }
    },
    computed: {
      errorState() {
        return this.formattedErrors && this.formattedErrors.length > 0;
      },
      formattedErrors() {
        var errs = [];

        for (var i = 0; i < this.errors.length; i++) {
          var elementToAdd = { propertyAbbreviation: this.errors[i].propertyAbbreviation, error: this.errors[i].error };
          if (this.errors[i].timesDisplayed > 1) {
            for (var j = 0; j < this.errors[i].timesDisplayed; j++) {
              errs.push(elementToAdd);
            }
          } else {
            errs.push(elementToAdd);
          }
        }

        return errs;
      },
      methodText() {
        switch (this.method) {
          case 1: return "using 'Flat Fee'";
          case 2: return "using 'Price Per Unit'";
          case 3: return "using '% Allocation by Unit Count'";
          case 4: return "using 'Assumptions Only (No Calculations)'";
        }

        return '';
      },
      applicationText() {
        return this.past ? ':' : 'is ready to apply.'
      },
      showPercent() {
        return this.method === 3;
      },
      totalUnits() {
        return (this.bulkApplications || [0]).map(a => a.units || 0).reduce((a, b) => (a || 0) + (b || 0));
      },
      totalPercent() {
        return (this.bulkApplications || [0]).map(a => a.percent || 0).reduce((a, b) => (a || 0) + (b || 0));
      },
      totalAmount() {
        return (this.bulkApplications || [0]).map(a => a.amount || 0).reduce((a, b) => (a || 0) + (b || 0));
      },
      saveDisabled() {
        return this.isSaveButtonDisabled || ((this.assumptionStatementInternal && this.assumptionStatementInternal.length > 100) || (this.method === 4 && (!this.assumptionStatementInternal || this.assumptionStatementInternal.length < 3)))
      }
    },
    mounted() {
      this.assumptionStatementInternal = this.assumptionStatement
    }
  }
</script>

<style lang="scss" scoped>
  .theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
    border-top: thin solid rgba(0, 0, 0, 0.12);
    border-left: thin solid rgba(0, 0, 0, 0.12);
  }
  .theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr > th:last-child {
    border-right: thin solid rgba(0, 0, 0, 0.12);
  }

  .theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr > td:not(.v-data-table__mobile-row) {
    border-left: thin solid rgba(0, 0, 0, 0.12);
  }
  .theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr > td:last-child {
    border-right: thin solid rgba(0, 0, 0, 0.12);
  }
  .theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr:last-child > td {
    border-bottom: thin solid rgba(0, 0, 0, 0.12);
  }
  span.bulk-creation-orange {
    color: var(--v-resOrange-darken2);
  }

  tbody tr td,
  thead tr th {
      text-align: center !important;
  }
  thead tr th {
    font-size: 12px !important;
    font-weight: bold;
    background-color: #DFEAF0;
  }
  .text-left {
      text-align: left !important;
  }
  .subtle {
      font-size: 12px !important;
  }
  .short {
      max-width: 100px;
  }
  .no-border-left {
      border-left: none !important;
  }
  .no-border-right {
      border-right: none !important;
  }
  </style>
